import { Schema } from "effect"
import { TOPIC_FAMILIES } from "./topics/index.ts"

// Messages exchanged over the `/ws` WebSocket. Frames are JSON text, tagged by
// `_tag`. Ping, Pong, Welcome and Heartbeat carry epoch milliseconds; topic data
// uses epoch seconds and decimal strings (docs/design/0005).
//
// Live data flows through topics: the client sends Subscribe{topic}; the server
// answers SubscribeFailed, or a Snapshot followed by Updates (seq = previous + 1)
// and Status frames. On a seq gap or a Resync the client resubscribes and gets a
// fresh Snapshot. Old clients never send Subscribe, so the change is additive.

export const WS_PATH = "/ws"

/** At most this many topics per connection (SubscribeFailed{TooManySubscriptions} beyond it). */
export const MAX_SUBSCRIPTIONS_PER_CONNECTION = 32

/** Any family's topic, e.g. { _tag: "Account", accountId }. Derived from TOPIC_FAMILIES. */
export const Topic = Schema.Union(TOPIC_FAMILIES.map((family) => family.topic))
export type Topic = typeof Topic.Type

/** A topic's key, e.g. "account:acc_1" (topicKey in topics/index.ts). */
export const TopicKey = Schema.String.pipe(Schema.check(Schema.isMinLength(1), Schema.isMaxLength(200)))

const TopicSnapshotData = Schema.Union(TOPIC_FAMILIES.map((family) => family.snapshot))
const TopicUpdateData = Schema.Union(TOPIC_FAMILIES.map((family) => family.update))
const TopicStatusData = Schema.Union(TOPIC_FAMILIES.map((family) => family.status))

// --- Client → server ----------------------------------------------------------------

export const Ping = Schema.TaggedStruct("Ping", { t: Schema.Number })
export const Subscribe = Schema.TaggedStruct("Subscribe", { topic: Topic })
export const Unsubscribe = Schema.TaggedStruct("Unsubscribe", { key: TopicKey })

export const ClientMessage = Schema.Union([Ping, Subscribe, Unsubscribe])
export type ClientMessage = typeof ClientMessage.Type

// --- Server → client ----------------------------------------------------------------

export const Welcome = Schema.TaggedStruct("Welcome", {
  version: Schema.String,
  serverTime: Schema.Number
})
export const Pong = Schema.TaggedStruct("Pong", {
  t: Schema.Number,
  serverTime: Schema.Number
})
export const Heartbeat = Schema.TaggedStruct("Heartbeat", {
  seq: Schema.Number,
  serverTime: Schema.Number
})

/** The topic's state as of update `seq`, sent once per subscription. Updates continue at seq + 1. */
export const TopicSnapshot = Schema.TaggedStruct("Snapshot", { key: TopicKey, seq: Schema.Int, data: TopicSnapshotData })
/** One change. The same frame (same bytes) goes to every subscriber of the key. */
export const TopicUpdate = Schema.TaggedStruct("Update", { key: TopicKey, seq: Schema.Int, data: TopicUpdateData })
/** Latest-wins data, coalesced; each replaces the previous one. */
export const TopicStatus = Schema.TaggedStruct("Status", { key: TopicKey, data: TopicStatusData })

/** SlowConsumer: this subscriber fell behind and frames were dropped. Restarted: the producer restarted. */
export const ResyncReason = Schema.Literals(["SlowConsumer", "Restarted"])
/** The subscription ended; subscribe again for a fresh snapshot. */
export const TopicResync = Schema.TaggedStruct("Resync", { key: TopicKey, reason: ResyncReason })

export const SubscribeFailedReason = Schema.Literals(["Forbidden", "NotFound", "UnknownTopic", "TooManySubscriptions"])
export type SubscribeFailedReason = typeof SubscribeFailedReason.Type
export const SubscribeFailed = Schema.TaggedStruct("SubscribeFailed", { key: TopicKey, error: SubscribeFailedReason })

export const ServerMessage = Schema.Union([
  Welcome,
  Pong,
  Heartbeat,
  TopicSnapshot,
  TopicUpdate,
  TopicStatus,
  TopicResync,
  SubscribeFailed
])
export type ServerMessage = typeof ServerMessage.Type
